/**
 * AdminLiveInventoryManual
 * ------------------------
 * Hand-key items straight onto the public Live Backorder list. Each item
 * carries its own region and merges into the public merged list alongside
 * the uploaded supplier sources.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, X, Check, PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const BLANK = {
  manufacturer: "",
  mfr_number: "",
  description: "",
  quantity: "",
  um: "",
  eta: "",
  region: "National",
};

function Field({ label, field, placeholder, state, setState, testid }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <Input
        value={state[field]}
        placeholder={placeholder}
        onChange={(e) => setState((p) => ({ ...p, [field]: e.target.value }))}
        className="h-9 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

export default function AdminLiveInventoryManual({ token, onChanged }) {
  const [rows, setRows] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(BLANK);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/live-inventory/manual`, { headers });
      setRows(r.data?.rows || []);
      setRegions(r.data?.regions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.description.trim()) {
      toast.error("Description is required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/live-inventory/manual`, draft, { headers });
      toast.success("Item added to the live list.");
      setDraft({ ...BLANK, region: draft.region });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row._row_id);
    setEditDraft({
      manufacturer: row["Manufacturer"] || "",
      mfr_number: row["MFR #"] || "",
      description: row["Description"] || "",
      quantity: row["Quantity"] || "",
      um: row["U/M"] || "",
      eta: row["ETA"] || "",
      region: row["Region"] || "National",
    });
  };

  const saveEdit = async () => {
    if (!editDraft.description.trim()) {
      toast.error("Description is required.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/live-inventory/manual/${editingId}`, editDraft, { headers });
      toast.success("Item updated.");
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove "${row["Description"]}" from the live list?`)) return;
    try {
      await axios.delete(`${API}/api/admin/live-inventory/manual/${row._row_id}`, { headers });
      toast.success("Item removed.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 space-y-4" data-testid="live-inv-manual-panel">
      <div>
        <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
          <PackagePlus className="w-4 h-4 text-cyan-700" />
          Manual Entries &mdash; add items by hand
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          These rows merge into the public live list immediately. Each item has its own region.
        </p>
      </div>

      {/* Add form */}
      <div className="grid gap-3 md:grid-cols-7 items-end bg-slate-50 rounded-lg p-3 border border-slate-200">
        <Field label="Manufacturer" field="manufacturer" placeholder="e.g. 3M" state={draft} setState={setDraft} testid="manual-input-manufacturer" />
        <Field label="MFR #" field="mfr_number" placeholder="e.g. 930415" state={draft} setState={setDraft} testid="manual-input-mfr" />
        <div className="md:col-span-2">
          <Field label="Description *" field="description" placeholder="Product description" state={draft} setState={setDraft} testid="manual-input-description" />
        </div>
        <Field label="Quantity" field="quantity" placeholder="e.g. 100" state={draft} setState={setDraft} testid="manual-input-quantity" />
        <Field label="U/M" field="um" placeholder="CS / EA / BX" state={draft} setState={setDraft} testid="manual-input-um" />
        <Field label="ETA" field="eta" placeholder="IN STOCK / 2 wks" state={draft} setState={setDraft} testid="manual-input-eta" />
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Region</label>
          <select
            value={draft.region}
            onChange={(e) => setDraft((p) => ({ ...p, region: e.target.value }))}
            className="h-9 text-sm rounded-md border border-slate-300 bg-white px-2"
            data-testid="manual-input-region"
          >
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button
          onClick={add}
          disabled={saving}
          className="bg-cyan-600 hover:bg-cyan-700 text-white h-9"
          data-testid="manual-add-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Add item
        </Button>
      </div>

      {/* Existing manual rows */}
      {loading ? (
        <div className="text-sm text-slate-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading manual items…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="manual-empty">No manual items yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manual-rows-table">
            <thead>
              <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-3">Manufacturer</th>
                <th className="text-left py-2 pr-3">MFR #</th>
                <th className="text-left py-2 pr-3">Description</th>
                <th className="text-left py-2 pr-3">Qty</th>
                <th className="text-left py-2 pr-3">U/M</th>
                <th className="text-left py-2 pr-3">ETA</th>
                <th className="text-left py-2 pr-3">Region</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = editingId === row._row_id;
                if (isEditing) {
                  return (
                    <tr key={row._row_id} className="border-b border-slate-100 bg-amber-50/50" data-testid={`manual-row-edit-${row._row_id}`}>
                      {["manufacturer", "mfr_number", "description", "quantity", "um", "eta"].map((f) => (
                        <td key={f} className="py-2 pr-2">
                          <Input
                            value={editDraft[f]}
                            onChange={(e) => setEditDraft((p) => ({ ...p, [f]: e.target.value }))}
                            className="h-8 text-sm"
                            data-testid={`manual-edit-${f}`}
                          />
                        </td>
                      ))}
                      <td className="py-2 pr-2">
                        <select
                          value={editDraft.region}
                          onChange={(e) => setEditDraft((p) => ({ ...p, region: e.target.value }))}
                          className="h-8 text-sm rounded-md border border-slate-300 bg-white px-2"
                          data-testid="manual-edit-region"
                        >
                          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white mr-1.5" onClick={saveEdit} disabled={saving} data-testid="manual-edit-save">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} data-testid="manual-edit-cancel">
                          <X className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={row._row_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`manual-row-${row._row_id}`}>
                    <td className="py-2 pr-3 text-slate-700">{row["Manufacturer"] || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-800">{row["MFR #"] || "—"}</td>
                    <td className="py-2 pr-3 text-slate-900 font-medium">{row["Description"] || "—"}</td>
                    <td className="py-2 pr-3 text-slate-700">{row["Quantity"] || "—"}</td>
                    <td className="py-2 pr-3 text-slate-700">{row["U/M"] || "—"}</td>
                    <td className="py-2 pr-3 text-slate-700">{row["ETA"] || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className="text-[10px] font-bold text-cyan-800 bg-cyan-50 ring-1 ring-cyan-500/40 px-2 py-0.5 rounded-full">
                        {row["Region"] || "National"}
                      </span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1.5" onClick={() => startEdit(row)} data-testid={`manual-edit-btn-${row._row_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:border-red-500"
                        onClick={() => remove(row)}
                        data-testid={`manual-delete-btn-${row._row_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
